import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseService, BreadcrumbService, GridOptionsExt, QueryComponent, RouteFns } from '@core';
import { Account, ProximityRightEnum } from '@models';
import { UtilFns } from '@utils';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { accountRoutes } from './account.routes';
import { AccountDbQueryService } from './services/account-db-query.service';
import { ProgramMenuService, ProgramMenuContext } from './services/program-menu.service';

@Component({
  selector: 'prox-account-manager',
  templateUrl: './account-manager.component.html',
  standalone: false,
  // encapsulation: ViewEncapsulation.None,
  // Styles now handled by global submenu-quickbooks.scss
  //
  // EXCEPTION — one rule kept local. PanelMenu renders a separator item as
  // <li class="p-menuitem-separator">, but neither the PrimeNG Aura preset nor
  // submenu-quickbooks.scss gives that class any rule, so it comes out as an empty
  // zero-height <li> and the separator is invisible. This is a gap in the QuickBooks
  // submenu theme rather than anything specific to this page. It is held here because
  // submenu-quickbooks.scss is protected and shared with Uniformax; move it there once
  // that parity change is approved. It reuses the theme's own border token, so it does
  // not introduce a color and follows dark mode for free.
  styles: [
    `
      :host ::ng-deep .layout-submenu .p-menuitem-separator {
        border-top: 1px solid var(--qb-submenu-header-border);
        margin: 6px 0;
      }
    `,
  ],
})
export class AccountManagerComponent extends QueryComponent implements OnDestroy {
  items: MenuItem[] = [];
  private baseItems: MenuItem[] = []; // Store the base menu items without program sub-items

  accountId?: string;
  accountName?: string;
  rightsMap: { [path: string]: ProximityRightEnum[] } = {};
  isValidating = false;

  account?: Account;
  accountForActivation!: Account | undefined;

  errsGridOptions: GridOptionsExt = {};

  isSidebarCollapsed = false;

  private programMenuSubscription?: Subscription;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    override dbQueryService: AccountDbQueryService,
    private programMenuService: ProgramMenuService,
  ) {
    super(baseService, route, dbQueryService);
    RouteFns.populateRightsMap('/account', accountRoutes, this.rightsMap);

    // Subscribe to program menu changes
    this.programMenuSubscription = this.programMenuService.menuContext$.subscribe((context) => {
      this.updateProgramsMenu(context);
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.programMenuSubscription?.unsubscribe();
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  private updateProgramsMenu(context: ProgramMenuContext | null) {
    if (!this.baseItems.length) return;

    // Find the Programs menu item index
    const programsIndex = this.baseItems.findIndex((item) => item.label?.includes('Programs') && !item.label?.includes('Preparation'));

    if (programsIndex === -1) return;

    // Create a new items array based on baseItems (deep copy to allow mutation)
    const newItems = this.baseItems.map((item) => ({ ...item }));

    if (context) {
      // Program is being edited - collapse the other menu sections
      for (let i = 0; i < programsIndex; i++) {
        newItems[i].expanded = false;
      }

      // Add sub-items under Programs header
      // Include "Back to Programs" as the first item
      const backToListItem: MenuItem = {
        label: '← Back to Programs',
        icon: 'pi pi-arrow-left',
        routerLink: ['/account/accounts', this.accountId, 'manage', 'programs'],
        styleClass: 'back-to-list-item',
      };

      // Open the Program submenu immediately. The operator selected this Program, so the
      // sub-items are what they came for. A collapsed menu makes them click again.
      newItems[programsIndex] = {
        label: `<span class="text-l font-bold">Program: ${context.programName}</span>`,
        escape: false,
        expanded: true,
        items: [backToListItem, ...context.items],
      };
    } else {
      // No program selected - expand all menu sections back to default
      for (let i = 0; i < programsIndex; i++) {
        newItems[i].expanded = true;
      }

      // Restore original Programs link
      newItems[programsIndex] = {
        label: '<span class="text-l font-bold">Programs</span>',
        escape: false,
        icon: 'fa-sharp-duotone fa-regular fa-circle-nodes',
        routerLink: ['/account/accounts', this.accountId, 'manage', 'programs'],
      };
    }

    this.items = newItems;
  }

  async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');

    const result = await this.dbQueryService.fetchEntityByKey(Account, this.accountId, true);
    this.accountName = (result.entity as Account)?.name;

    if (this.authUser?.hasRight(ProximityRightEnum.IsGlobalAdminForAccounts)) {
      this.breadcrumbService.setItems([
        { label: 'List Accounts', routerLink: ['/account/accounts'] },
        { label: 'Manage Account: ' + this.accountName },
      ]);
    } else {
      this.breadcrumbService.setItems([{ label: this.accountName }]);
    }

    /* if ((!this.authUser?.hasRight(ProximityRightEnum.CanManageAccountAdministration)) || this.activationService.isActivated) { */
    if (
      !this.authUser?.hasRight(
        ProximityRightEnum.CanManageAccountAdministration,
        ProximityRightEnum.CanManageAccountOrganization,
        ProximityRightEnum.CanEditProgram,
      )
    ) {
      this.items = [];
      this.isPageReady = true;
      return;
    }

    this.items = [
      {
        label: '<span class="text-l font-bold">Administration</span>',
        escape: false,
        expanded: false,
        items: [
          {
            label: 'Information',
            icon: 'pi pi-fw pi-cog',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-info'],
          },
          {
            label: 'Administrators',
            icon: 'fa-duotone fa-solid fa-user-tie-hair-long',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-administrators'],
          },
          {
            label: 'Administrator Groups',
            icon: 'fa-sharp-duotone fa-solid fa-people-group',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-admin-groups'],
          },
          {
            label: 'Roles',
            icon: 'fa-sharp-duotone fa-solid fa-key',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-roles'],
          },
          {
            label: 'Notification Submission Status',
            icon: 'pi pi-fw pi-cog',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'notificationSubmissionStatus'],
          },
        ],
      },
      {
        label: '<span class="text-l font-bold">Organization</span>',
        escape: false,
        expanded: false,
        items: [
          {
            label: 'Account Users',
            icon: 'fa-duotone fa-light fa-users',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-users'],
          },
          {
            label: 'Budget Product Tags',
            icon: 'fa-regular fa-tags',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'budget-product-type-tags'],
          },
          {
            label: 'Budgets',
            icon: 'fa-sharp fa-regular fa-piggy-bank',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-budgets'],
          },
          {
            label: 'Shipping User Groups',
            icon: 'pi pi-fw pi-truck',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'shipping-user-groups'],
          },
          {
            label: 'Shipping Addresses',
            icon: 'fa-sharp fa-solid fa-address-book',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'shipping-account-addresses'],
          },
          {
            label: 'Billing Addresses',
            icon: 'fa-sharp fa-regular fa-file-invoice-dollar',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'billing-account-addresses'],
          },

          {
            label: 'Product Catalog',
            icon: 'ffa-sharp fa-regular fa-book-open',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-price-lists'],
          },
        ],
      },
      {
        label: '<span class="text-l font-bold">Communication</span>',
        escape: false,
        expanded: false,
        items: [
          {
            label: 'Notification Templates',
            icon: 'fa-sharp-duotone fa-regular fa-copy',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-notification-templates'],
          },
          {
            label: 'Notification Settings',
            icon: 'fa-sharp fa-regular fa-gear',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-notification-settings'],
          },
        ],
      },
      {
        label: '<span class="text-l font-bold">Program Preparation</span>',
        escape: false,
        expanded: false,
        items: [
          {
            label: 'Product Tags',
            icon: 'fa-sharp fa-regular fa-tags',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'program-product-type-tags'],
          },
          // {
          //   label: 'Kit Tags',
          //   icon: 'fa-duotone fa-regular fa-suitcase',
          //   routerLink: ['/account/accounts', ':accountId', 'manage', 'program-kit-tags'],
          // },
          {
            label: 'Program User Groups',
            icon: 'fa-duotone fa-solid fa-user-group',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'program-user-groups'],
          },
          {
            label: 'Purchase Orders',
            icon: 'fa-solid fa-money-check-dollar-pen',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-purchase-orders'],
          },
          {
            label: 'Procurement Cards',
            icon: 'pi pi-fw pi-credit-card',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'account-procurement-cards'],
          },
          {
            label: 'Proximity Organization',
            icon: 'fa-regular fa-sitemap',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'approval-trees'],
          },
        ],
      },
      {
        label: '<span class="text-l font-bold">Programs</span>',
        escape: false,
        icon: 'fa-sharp-duotone fa-regular fa-circle-nodes',
        routerLink: ['/account/accounts', ':accountId', 'manage', 'programs'],
      },
      {
        label: '<span class="text-l font-bold">Documents</span>',
        escape: false,
        expanded: false,
        items: [
          {
            label: 'How to Import Users',
            icon: 'fa-duotone fa-solid fa-file-lines',
            routerLink: ['/account/accounts', ':accountId', 'manage', 'documents', 'how-to-import-users'],
          },
        ],
      },
    ];

    const paramMap = { ':accountId': this.accountId };
    RouteFns.processMenuItems(this.items, paramMap, this.rightsMap, this.authUser);
    RouteFns.expandActiveSection(this.items, this.router.url);

    // Store the base items for later modification when program is selected
    this.baseItems = [...this.items];

    // Check if there's already a program context (e.g., page refresh while editing a program)
    const currentContext = this.programMenuService.getCurrentContext();
    if (currentContext) {
      this.updateProgramsMenu(currentContext);
    }

    this.isPageReady = true;
  }
}
